import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';
import { formatDollarAmount, formatLargeDollarAmount, formatPercentage } from 'src/utils/formatting';

import { BENCHMARK_NAME_QUALIFIERS } from './constants';

export const EM_DASH = '—';

export function shortBenchmarkLabel(label: string): string {
  const words = label.trim().split(/\s+/);
  while (words.length > 2 && BENCHMARK_NAME_QUALIFIERS.includes(words[words.length - 1] ?? '')) {
    words.pop();
  }
  return words.join(' ');
}

export function isPresent(value: string | null | undefined): value is string {
  return !isNil(value) && value.trim() !== '';
}

export function formatMonthAndYear(isoDate: string | null | undefined): string | null {
  if (isNil(isoDate)) {
    return null;
  }
  const parsed = DateTime.fromISO(isoDate);
  return parsed.isValid ? parsed.toFormat('LLLL yyyy') : null;
}

export function toFiniteNumber(value: string | number | null | undefined): number | null {
  if (isNil(value) || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function safeMoney(amount: number | string | null | undefined, showDecimal = false) {
  if (isNil(amount) || amount === '') {
    return EM_DASH;
  }
  const parsed = Number(amount);
  if (!Number.isFinite(parsed)) {
    return EM_DASH;
  }
  const magnitude = formatDollarAmount(Math.abs(parsed), showDecimal);
  if (parsed < 0 && !roundsAwayToZero(magnitude)) {
    return `-${magnitude}`;
  }
  return magnitude;
}

export function safeLargeMoney(amount: number | string | null | undefined) {
  if (isNil(amount) || amount === '') {
    return EM_DASH;
  }
  const parsed = Number(amount);
  if (!Number.isFinite(parsed)) {
    return EM_DASH;
  }
  return formatLargeDollarAmount(parsed);
}

export function safePercent(value: number | string | null | undefined, decimalPlaces = 2) {
  if (isNil(value) || value === '') {
    return EM_DASH;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return EM_DASH;
  }
  return formatPercentage(parsed, decimalPlaces);
}

export function roundsAwayToZero(formattedMagnitude: string) {
  return !/[1-9]/.test(formattedMagnitude);
}

function withSign(parsed: number, formattedMagnitude: string) {
  if (roundsAwayToZero(formattedMagnitude)) {
    return formattedMagnitude;
  }
  return parsed < 0 ? `-${formattedMagnitude}` : `+${formattedMagnitude}`;
}

export function safeSignedMoney(amount: number | string | null | undefined) {
  if (isNil(amount) || amount === '') {
    return EM_DASH;
  }
  const parsed = Number(amount);
  if (!Number.isFinite(parsed)) {
    return EM_DASH;
  }
  return withSign(parsed, formatDollarAmount(Math.abs(parsed)));
}

export function safeSignedPercent(value: number | string | null | undefined, decimalPlaces = 2) {
  if (isNil(value) || value === '') {
    return EM_DASH;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return EM_DASH;
  }
  return withSign(parsed, formatPercentage(Math.abs(parsed), decimalPlaces));
}

export type ChangeDirection = 'up' | 'down' | 'flat';

export function changeDirection(value: number | null | undefined): ChangeDirection {
  if (isNil(value) || !Number.isFinite(value) || value === 0) {
    return 'flat';
  }
  return value > 0 ? 'up' : 'down';
}

export function chartSpansMultipleYears(series: readonly { readonly date: string }[]): boolean {
  const years = series.map((point) => DateTime.fromISO(point.date)).filter((parsed) => parsed.isValid);
  const first = years.at(0);
  const last = years.at(-1);
  if (isNil(first) || isNil(last)) {
    return false;
  }
  return last.year !== first.year;
}

export function formatChartTickDate(isoDate: string, withYear: boolean): string {
  const parsed = DateTime.fromISO(isoDate);
  if (!parsed.isValid) {
    return isoDate;
  }
  return parsed.toFormat(withYear ? 'LLL yyyy' : 'LLL d');
}
